import {useEffect, useState} from 'react';
import {ChevronRight} from 'lucide-react';
import {
  Badge,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {useAllChannels, useItems} from '@/api/hooks';
import {type ChannelAggregate, type Item, type ItemsQuery} from '@/api/items';
import {type FlagColor} from '@/api/flags';
import {ItemsCollection} from '@/components/items/ItemsCollection';

// How many of a channel's items one expansion loads. A channel can hold thousands, so the Items
// tab (with its paging) is where to read past this.
const CHANNEL_ITEMS_PAGE_SIZE = 100;

// The most channels the tab lists. The API ranks by item count and cuts here, so a count at this
// number means "at least", and a channel past it is reached by narrowing the filters.
export const CHANNEL_LIMIT = 100;

// What the Channels tab narrows by: the Content page's search, platform and filters, but none of
// the paging or sort that belong to the item list itself.
export type ContentChannelsQuery = Omit<ItemsQuery, 'page' | 'page_size' | 'sort' | 'channel_key'>;

// The one channel query, so the tab's header count and the list share a cache entry.
export const useContentChannels = (query: ContentChannelsQuery, enabled = true) =>
  useAllChannels({...query, limit: CHANNEL_LIMIT}, enabled);

export type ContentChannelsProps = {
  readonly query: ContentChannelsQuery;
  readonly sort: ItemsQuery['sort'];
  readonly selectedItemIds: ReadonlySet<string>;
  readonly onToggleCheck: (id: string) => void;
  readonly onClassify: (item: Item) => void;
  readonly onSetFlag: (item: Item, color: FlagColor | null) => void;
  readonly onShowDetails: (item: Item, items: readonly Item[]) => void;
  // Hands the page every item a channel loads, so a selection can be acted on after the channel
  // that listed it is collapsed.
  readonly onItemsLoaded: (items: readonly Item[]) => void;
  readonly searchTerm?: string | undefined;
};

// The Content page's Channels tab: one collapsible section per channel, largest first. The
// headers come from one aggregate query; a channel's items are fetched only once it is opened.
export function ContentChannels({query, sort, ...rest}: ContentChannelsProps) {
  const {data, isLoading} = useContentChannels(query);
  const channels = data?.results ?? [];

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <Spinner />
        Loading…
      </div>
    );
  }

  if (channels.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No channels match</EmptyTitle>
          <EmptyDescription>Try clearing the search or the filters.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {channels.map((channel) => (
        <ChannelSection key={channel.key} channel={channel} query={query} sort={sort} {...rest} />
      ))}
    </div>
  );
}

function ChannelSection({
  channel,
  query,
  sort,
  selectedItemIds,
  onToggleCheck,
  onClassify,
  onSetFlag,
  onShowDetails,
  onItemsLoaded,
  searchTerm
}: ContentChannelsProps & {readonly channel: ChannelAggregate}) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible
      open={open}
      className="border-border bg-card block rounded-lg border"
      onOpenChange={setOpen}>
      <CollapsibleTrigger className="hover:text-foreground flex w-full items-center gap-2 px-3 py-2 text-left transition-colors">
        <ChevronRight className="text-muted-foreground size-4 shrink-0 transition-transform in-data-panel-open:rotate-90" />
        <span className="text-foreground min-w-0 truncate text-xs font-medium">
          {channel.label}
        </span>
        {channel.author_id ? (
          <span className="text-muted-foreground shrink-0 font-mono text-xs">
            {channel.author_id}
          </span>
        ) : null}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {channel.classified_count > 0 ? (
            <Badge variant="outline">{channel.classified_count.toLocaleString()} classified</Badge>
          ) : null}
          <span className="text-muted-foreground text-xs tabular-nums">
            {channel.total.toLocaleString()} {channel.total === 1 ? 'item' : 'items'}
          </span>
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        {open ? (
          <ChannelItems
            channelKey={channel.key}
            total={channel.total}
            query={query}
            sort={sort}
            selectedItemIds={selectedItemIds}
            searchTerm={searchTerm}
            onToggleCheck={onToggleCheck}
            onClassify={onClassify}
            onSetFlag={onSetFlag}
            onShowDetails={onShowDetails}
            onItemsLoaded={onItemsLoaded}
          />
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Mounted only while its channel is open, so a collapsed channel costs no request.
function ChannelItems({
  channelKey,
  total,
  query,
  sort,
  selectedItemIds,
  onToggleCheck,
  onClassify,
  onSetFlag,
  onShowDetails,
  onItemsLoaded,
  searchTerm
}: Omit<ContentChannelsProps, 'query'> & {
  readonly channelKey: string;
  readonly total: number;
  readonly query: ContentChannelsQuery;
}) {
  const parameters: ItemsQuery = {
    ...query,
    channel_key: channelKey,
    page_size: CHANNEL_ITEMS_PAGE_SIZE
  };
  if (sort) {
    parameters.sort = sort;
  }

  const {data, isLoading} = useItems(parameters);
  const items = data?.results;
  useEffect(() => {
    if (items) {
      onItemsLoaded(items);
    }
  }, [items, onItemsLoaded]);

  if (isLoading || !items) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-xs">
        <Spinner />
        Loading…
      </div>
    );
  }

  return (
    <div className="border-border flex flex-col gap-3 border-t p-3">
      <ItemsCollection
        items={items}
        selectedItemIds={selectedItemIds}
        onToggleCheck={onToggleCheck}
        onClassify={onClassify}
        onSetFlag={onSetFlag}
        onShowDetails={onShowDetails}
        {...(searchTerm ? {searchTerm} : {})}
      />
      {data?.has_more ? (
        <p className="text-muted-foreground text-xs">
          Showing the first {items.length.toLocaleString()} of {total.toLocaleString()}.
        </p>
      ) : null}
    </div>
  );
}
